/**
 * Pinia impersonation store (PR-R3 - SuperAdmin Phase 1).
 *
 * Owns the SuperAdmin "viewing-as-distributor" session state. Used by:
 *   - ImpersonationBanner.vue (visibility + label)
 *   - SuperAdminLayout / DistributorLayout (mount the banner)
 *   - SA Distributors list "Impersonate" button -> startImpersonation()
 *   - banner exit button -> stopImpersonation()
 *
 * Why a dedicated store (not part of useAccountStore):
 *   - Impersonation is orthogonal to account identity. The SA account is
 *     still the actor; the impersonation flag is a transient overlay.
 *     Keeping it separate avoids muddying account-loading code.
 *   - Component code asking "should I show the warning banner?" can
 *     subscribe to a single store with a tiny surface.
 *
 * Mechanism (token-stamp, NOT token-swap): the same bearer token carries
 * impersonation via the backend `tokens.impersonated_distributor_id`
 * column. There is no second token. This store holds only the local UI
 * state (banner visibility + label); the authoritative capability set
 * comes from the backend on the next request. See impersonationService.js
 * for the endpoint contract.
 *
 * Audit log integration:
 *   The backend impersonate endpoints append an `impersonation`-category
 *   audit row on start + stop. The store does NOT make a separate audit
 *   call - that would split the privilege transition across two API
 *   roundtrips and create an audit gap if the second fails.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
    startImpersonationApi,
    stopImpersonationApi,
    getImpersonationStatusApi,
    startClientImpersonationApi,
    stopClientImpersonationApi,
    getClientImpersonationStatusApi,
    startViewAsCustomerApi,
} from '@/services/impersonationService'
import { useDistributorSummaryStore } from '@/stores/distributorSummary'
import { useStoreReadinessStore } from '@/stores/storeReadiness'
import { useDecorationRollupStore } from '@/stores/decorationRollup'
import { announceSeatChange } from '@/utils/impersonationSeatSync'

// The status endpoint reports active + distributor_id but no name, and this
// store is memory-only -- the post-start window.location.reload() wipes it.
// We stash the human-readable name here at start so hydrate() can restore the
// banner label after the reload. The authoritative active/id always come from
// the backend (GET /impersonate/status); this is only the cosmetic label.
//
// Exported because requireToken.js reads the SAME key synchronously as a
// pre-fetch "am I booting into an impersonation session?" signal, to skip the
// doomed self-account fetch on the first load into an impersonation (see the
// IMPERSONATION_NAME_KEY consumer in router/requireToken.js). One source of
// truth for the breadcrumb key rather than two hardcoded string literals.
export const IMPERSONATION_NAME_KEY = 'impersonationDistributorName'
const NAME_KEY = IMPERSONATION_NAME_KEY

// CLIENT impersonation breadcrumb (card https://trello.com/c/8eq8uc8E). The
// client-status endpoint returns active + client_id but no NAME, and this
// store is memory-only (the post-start window.location.assign('/portal')
// wipes it). We stash the human-readable client name here at start so
// hydrate() can restore the banner label after that navigation. We ALSO
// keep writing the shared IMPERSONATION_NAME_KEY breadcrumb (below) so
// requireToken.js's synchronous "am I booting into an impersonation?"
// signal skips the doomed SA self-account fetch on the first portal load,
// exactly as it does for distributor impersonation.
export const IMPERSONATION_CLIENT_NAME_KEY = 'impersonationClientName'
const CLIENT_NAME_KEY = IMPERSONATION_CLIENT_NAME_KEY

// Post-stop exit target breadcrumb (card https://trello.com/c/k8m2iLzG). Client
// impersonation is entered from TWO surfaces now:
//   * the SA Clients list -> exit back to the SA app (/admin/clients), AND
//   * a distributor's "View as client" -> exit back to the Distributor app
//     (/distributor/clients), because a distributor has NO /admin access and
//     would be bounced by the /admin capability guard.
// Both share the SAME token column + client mode, so the ONLY thing that
// differs at exit is WHERE to land. We stash that here at start so the banner's
// onExit (and hydrate() after the portal navigation wipes memory) can restore
// it. When absent, the banner falls back to the SA target for backward
// compatibility with sessions started before this key existed.
export const IMPERSONATION_EXIT_TO_KEY = 'impersonationExitTo'
const EXIT_TO_KEY = IMPERSONATION_EXIT_TO_KEY
const VIEW_AS_EXIT_TO = '/distributor/clients'

export const useImpersonationStore = defineStore('impersonation', () => {
    // ---- state ----
    // ``mode`` is the single source of truth for WHICH kind of impersonation
    // is active: 'distributor' | 'client' | null. ``active`` stays "any
    // impersonation active" so existing distributor consumers are unchanged.
    const active = ref(false)
    const mode = ref(null)
    const distributorId = ref(null)
    const distributorName = ref('')
    const clientId = ref(null)
    const clientName = ref('')
    const startedAt = ref(null)
    // Where the banner exit should land AFTER stopping. Null => the banner
    // uses its default (the SA app). A distributor "View as client" session
    // sets '/distributor/clients'. Restored from localStorage on hydrate.
    const exitTo = ref(null)

    // ---- getters ----
    const isActive = computed(() => active.value)
    const isClientActive = computed(
        () => active.value && mode.value === 'client'
    )
    const isDistributorActive = computed(
        () => active.value && mode.value === 'distributor'
    )
    // A distributor's "View as client" session (as opposed to an SA client
    // impersonation): the only client-mode session that exits back to the
    // Distributor app. Read by the deep-link step after hydrate().
    const isViewAsCustomer = computed(
        () => isClientActive.value && exitTo.value === VIEW_AS_EXIT_TO
    )

    /**
     * Human-readable banner text. Empty string when inactive so callers
     * can use it in templates without v-if gymnastics. Client mode reads
     * "Viewing as {client} (client)"; distributor mode is unchanged.
     */
    const bannerText = computed(() => {
        if (!active.value) return ''
        if (mode.value === 'client') {
            const name = clientName.value || 'a client'
            return `Viewing as ${name} (client)`
        }
        const name = distributorName.value || 'another distributor'
        return `Viewing as ${name} (impersonation session)`
    })

    // ---- actions ----
    /**
     * Start an impersonation session.
     *
     * Calls the backend, then flips local state on success. State is not
     * touched if the API rejects, so a failed start cannot leave a
     * phantom "viewing-as" banner on the page.
     *
     * @param {number} id - Distributor (Tenant) id
     * @param {string} name - Display name for the banner
     * @param {{deeplink?: boolean}} [opts] - `deeplink: true` marks an
     *   automatic deep-link switch; the server tags its audit row. The same
     *   option is accepted by the other start actions and by stop. Without
     *   it the service call is exactly the manual one.
     */
    async function startImpersonation(id, name, opts) {
        await (opts ? startImpersonationApi(id, opts) : startImpersonationApi(id))
        // The seat lives on the token every tab shares: tell the others
        // (utils/impersonationSeatSync.js).
        announceSeatChange()
        active.value = true
        mode.value = 'distributor'
        distributorId.value = id
        distributorName.value = name || ''
        // Distributor and client impersonation are mutually exclusive
        // (enforced server-side); clear any client-mode local state.
        clientId.value = null
        clientName.value = ''
        localStorage.removeItem(CLIENT_NAME_KEY)
        // Distributor impersonation exits to the SA app (banner default);
        // clear any distributor-customer exit breadcrumb.
        exitTo.value = null
        localStorage.removeItem(EXIT_TO_KEY)
        startedAt.value = Date.now()
        // Breadcrumb the name so hydrate() can restore the banner label after
        // the page reload that DistributorList.doImpersonate triggers.
        if (name) {
            localStorage.setItem(NAME_KEY, name)
        } else {
            localStorage.removeItem(NAME_KEY)
        }
    }

    /**
     * Start a CLIENT impersonation session (card
     * https://trello.com/c/8eq8uc8E).
     *
     * Calls the backend, then flips local state to client mode on success.
     * State is untouched if the API rejects (e.g. the client has no active
     * primary contact -> 422), so a failed start cannot leave a phantom
     * banner. The caller (ClientsList Open button) navigates to /portal after
     * this resolves.
     *
     * Breadcrumbs BOTH the client name (for the banner after the portal
     * navigation) AND the shared IMPERSONATION_NAME_KEY (so requireToken.js
     * skips the doomed SA self-account fetch on the first portal load, the
     * same optimization distributor impersonation relies on).
     *
     * @param {number} id - Client id
     * @param {string} name - Display name for the banner
     */
    async function startClientImpersonation(id, name, opts) {
        await (opts ? startClientImpersonationApi(id, opts) : startClientImpersonationApi(id))
        announceSeatChange()
        active.value = true
        mode.value = 'client'
        clientId.value = id
        clientName.value = name || ''
        // Mutual exclusion: clear any distributor-mode local state.
        distributorId.value = null
        distributorName.value = ''
        startedAt.value = Date.now()
        // SA client impersonation exits back to the SA app (the banner's
        // default), so clear any distributor exit breadcrumb from a prior
        // session.
        exitTo.value = null
        localStorage.removeItem(EXIT_TO_KEY)
        if (name) {
            localStorage.setItem(CLIENT_NAME_KEY, name)
            localStorage.setItem(NAME_KEY, name)
        } else {
            localStorage.removeItem(CLIENT_NAME_KEY)
            localStorage.removeItem(NAME_KEY)
        }
    }

    /**
     * Start a distributor "View as client" session (card
     * https://trello.com/c/k8m2iLzG).
     *
     * The distributor-facing, OWNERSHIP-SCOPED sibling of
     * startClientImpersonation. It shares the SAME client mode + token column,
     * so the banner, hydrate(), and stop() all behave identically -- the ONLY
     * differences are:
     *   1. it calls the ownership-scoped /view-as-customer/start endpoint
     *      (backend 404s a cross-tenant client), and
     *   2. it records an exit target of /distributor/clients so the banner's
     *      "Stop" returns the distributor to the Distributor app rather than
     *      the SA app (a distributor has no /admin access).
     *
     * State is untouched if the API rejects (e.g. cross-tenant 404, inactive
     * 422), so a failed start cannot leave a phantom banner. The caller
     * (distributor Clients surface) navigates to /portal after this resolves.
     *
     * @param {number} id - Client id (must belong to the caller's distributor)
     * @param {string} name - Display name for the banner
     */
    async function startViewAsCustomer(id, name, opts) {
        await (opts ? startViewAsCustomerApi(id, opts) : startViewAsCustomerApi(id))
        announceSeatChange()
        active.value = true
        mode.value = 'client'
        clientId.value = id
        clientName.value = name || ''
        distributorId.value = null
        distributorName.value = ''
        startedAt.value = Date.now()
        // Exit back to the Distributor app -- NOT the SA console (/admin) nor
        // the portal.
        exitTo.value = VIEW_AS_EXIT_TO
        localStorage.setItem(EXIT_TO_KEY, exitTo.value)
        if (name) {
            localStorage.setItem(CLIENT_NAME_KEY, name)
            localStorage.setItem(NAME_KEY, name)
        } else {
            localStorage.removeItem(CLIENT_NAME_KEY)
            localStorage.removeItem(NAME_KEY)
        }
    }

    /**
     * Stop the active impersonation session.
     *
     * No-op when no session is active (defensive - the banner exit button
     * can race with a page navigation that already stopped).
     *
     * Also wipes the distributor-summary store so the sidebar doesn't
     * flash the impersonated tenant's sub-text after the user exits to
     * the true-SA view. The summary store has no effective-tenant data
     * to refetch in the true-SA case; ``clear()`` is the right verb.
     *
     * Also wipes the per-store readiness and decoration-rollup caches
     * (storeReadiness / decorationRollup). Both are already keyed by
     * storeId and epoch-guarded against late cross-tenant responses (see
     * each store's ``clear()``), so this isn't closing a realistic data
     * bleed by itself -- it's defense in depth: clearing every per-store
     * Pinia cache on impersonation stop, not just the one the banner
     * happens to read, so a future consumer of these stores doesn't have
     * to remember to add itself here individually.
     */
    async function stopImpersonation(opts) {
        if (!active.value) return
        // Route to the correct stop endpoint for the active mode. Client
        // impersonation lives on a different token column, so the client
        // stop endpoint clears it; calling the distributor stop for a client
        // session would leave the client stamp in place (trapping the SA).
        if (mode.value === 'client') {
            await (opts ? stopClientImpersonationApi(opts) : stopClientImpersonationApi())
        } else {
            await (opts ? stopImpersonationApi(opts) : stopImpersonationApi())
        }
        announceSeatChange()
        active.value = false
        mode.value = null
        distributorId.value = null
        distributorName.value = ''
        clientId.value = null
        clientName.value = ''
        startedAt.value = null
        exitTo.value = null
        localStorage.removeItem(NAME_KEY)
        localStorage.removeItem(CLIENT_NAME_KEY)
        localStorage.removeItem(EXIT_TO_KEY)
        useDistributorSummaryStore().clear()
        useStoreReadinessStore().clear()
        useDecorationRollupStore().clear()
    }

    /**
     * Re-derive impersonation state from the backend on app boot.
     *
     * The store is memory-only, and DistributorList.doImpersonate() reloads
     * the page right after start so the SPA picks up the impersonated
     * capability set. That reload wipes this store -- so without hydrate the
     * ImpersonationBanner (and its only "Stop impersonating" exit affordance)
     * never renders in the distributor app, trapping the SA with no way back.
     *
     * GET /impersonate/status is authoritative for active + distributorId
     * (it reads the token's `impersonated_distributor_id`). The banner label
     * is restored from the localStorage breadcrumb written at start; if it's
     * missing (e.g. impersonation started on another device), the banner
     * falls back to "another distributor".
     *
     * Boot must never crash on this: a 401 (logged out) or network error
     * leaves the store inactive and resolves quietly.
     */
    async function hydrate() {
        try {
            const { data } = await getImpersonationStatusApi()
            if (data && data.active) {
                active.value = true
                mode.value = 'distributor'
                distributorId.value = data.distributor_id ?? null
                distributorName.value = localStorage.getItem(NAME_KEY) || ''
                clientId.value = null
                clientName.value = ''
                exitTo.value = null
                startedAt.value = startedAt.value || Date.now()
                return
            }
            // Not impersonating a distributor -> check for a CLIENT session
            // (mutually exclusive server-side, so at most one is active).
            const { data: clientData } = await getClientImpersonationStatusApi()
            if (clientData && clientData.active) {
                active.value = true
                mode.value = 'client'
                clientId.value = clientData.client_id ?? null
                clientName.value =
                    localStorage.getItem(CLIENT_NAME_KEY) || ''
                distributorId.value = null
                distributorName.value = ''
                // Restore the exit target so the banner "Stop" lands on the
                // right app after the portal navigation wiped memory. Absent
                // (SA client impersonation, or a pre-key session) => null, and
                // the banner falls back to the SA target.
                exitTo.value = localStorage.getItem(EXIT_TO_KEY) || null
                startedAt.value = startedAt.value || Date.now()
                return
            }
            // Neither active -> fully clear.
            active.value = false
            mode.value = null
            distributorId.value = null
            distributorName.value = ''
            clientId.value = null
            clientName.value = ''
            startedAt.value = null
            exitTo.value = null
            localStorage.removeItem(NAME_KEY)
            localStorage.removeItem(CLIENT_NAME_KEY)
            localStorage.removeItem(EXIT_TO_KEY)
        } catch {
            // Boot resilience: never let a status probe failure crash the app.
            active.value = false
            mode.value = null
            exitTo.value = null
        }
    }

    return {
        // state
        active,
        mode,
        distributorId,
        distributorName,
        clientId,
        clientName,
        startedAt,
        exitTo,
        // getters
        isActive,
        isClientActive,
        isDistributorActive,
        isViewAsCustomer,
        bannerText,
        // actions
        startImpersonation,
        startClientImpersonation,
        startViewAsCustomer,
        stopImpersonation,
        hydrate,
    }
})
